import type { ExperienceThemeTokens } from './theme.tokens';
import type { FullThemeTokens } from './theme.full';
import { DEFAULT_EXTENDED_CSS_VARS } from './theme.extended.defaults';
import { flattenThemeTokens, type NestedThemeTokens } from './flatten-theme-tokens';

export type ExperienceThemeMapping = {
    tokens: Partial<FullThemeTokens>;
    cssVarOverrides: Record<string, string>;
};

/** Mapeo de claves Experience → tokens semánticos del core theme. */
const EXPERIENCE_TO_CORE_TOKEN: Partial<Record<string, keyof FullThemeTokens>> = {
    COLOR_PRIMARY: 'primary',
    COLOR_PRIMARY_DARKER: 'primaryDarker',
    COLOR_PRIMARY_LIGHTER: 'primaryLighter',
    COLOR_PRIMARY_LIGHTEST: 'primaryLightest',
    COLOR_ACCENT: 'accent',
    COLOR_ACCENT_LIGHTER: 'secondaryLighter',
    COLOR_ACCENT_DARKER: 'secondaryDarker',
    COLOR_SECONDARY: 'secondary',
    COLOR_SECONDARY_LIGHTER: 'secondaryLighter',
    COLOR_SECONDARY_DARKER: 'secondaryDarker',
    COLOR_ERROR: 'error',
    COLOR_ERROR_LIGHTER: 'errorLighter',
    COLOR_ERROR_DARKER: 'errorDarker',
    COLOR_TEXT_PRIMARY: 'textPrimary',
    COLOR_TEXT_SECONDARY: 'textSecondary',
    COLOR_BACKGROUND: 'background',
    COLOR_BACKGROUND_SECONDARY: 'backgroundSecondary',
    BORDER_RADIUS: 'borderRadius',
    FONT_FAMILY: 'fontFamily',
    FONT_FAMILY_PRIMARY: 'fontFamily',
    STATUS_SCHEDULED: 'statusScheduled',
    STATUS_SCHEDULED_BG: 'statusScheduledBg',
    STATUS_DONE: 'statusDone',
    STATUS_DONE_BG: 'statusDoneBg',
    STATUS_IN_PROGRESS: 'statusInProgress',
    STATUS_IN_PROGRESS_BG: 'statusInProgressBg',
    STATUS_NOT_DONE: 'statusNotDone',
    STATUS_NOT_DONE_BG: 'statusNotDoneBg',
    STATUS_INFORMED: 'statusInformed',
    STATUS_INFORMED_BG: 'statusInformedBg',
    STATUS_BILLABLE: 'statusBillable',
    STATUS_BILLABLE_BG: 'statusBillableBg',
    STATUS_IN_PROCESS: 'statusInProcess',
    STATUS_DEBITED_BG: 'statusDebitedBg',
    STATUS_AUTHORIZED: 'statusAuthorized',
    STATUS_AUTHORIZED_BG: 'statusAuthorizedBg',
    STATUS_EXPIRING_SOON: 'statusExpiringSoon',
    STATUS_EXPIRING_SOON_BG: 'statusExpiringSoonBg',
    STATUS_EXPIRED: 'statusExpired',
    STATUS_EXPIRED_BG: 'statusExpiredBg',
    STATUS_UNAUTHORIZED: 'statusUnauthorized',
    STATUS_UNAUTHORIZED_BG: 'statusUnauthorizedBg',
    STATUS_PROVIDER_ACTIVE: 'statusProviderActive',
    STATUS_PROVIDER_INACTIVE: 'statusProviderInactive',
    STATUS_PROVIDER_POSSIBLE: 'statusProviderPossible',
    STATUS_PROVIDER_CANCELLED: 'statusProviderCancelled',
    STATUS_PROVIDER_ACTIVE_BG: 'statusProviderActiveBg',
    STATUS_PROVIDER_INACTIVE_BG: 'statusProviderInactiveBg',
    STATUS_PROVIDER_POSSIBLE_BG: 'statusProviderPossibleBg',
    STATUS_PROVIDER_CANCELLED_BG: 'statusProviderCancelledBg',
    STATUS_SUCCESS: 'statusSuccess',
    STATUS_SUCCESS_BG: 'statusSuccessBg',
    STATUS_WARNING: 'statusWarning',
    STATUS_WARNING_BG: 'statusWarningBg',
    STATUS_INFO: 'statusInfo',
    STATUS_INFO_BG: 'statusInfoBg',
    STATUS_NEUTRAL: 'statusNeutral',
    STATUS_NEUTRAL_BG: 'statusNeutralBg',
    STATUS_DISABLED: 'statusDisabled',
    STATUS_DISABLED_BG: 'statusDisabledBg',
    MAP_PATIENT_ACTIVE: 'mapPatientActive',
    MAP_PATIENT_INACTIVE: 'mapPatientInactive',
    MAP_PROVIDER_ACTIVE: 'mapProviderActive',
    MAP_PROVIDER_INACTIVE: 'mapProviderInactive',
    COLOR_WHITE: 'white',
    COLOR_BLACK: 'black',
    COLOR_GREY: 'grey',
    COLOR_GREY_LIGHT: 'greyLight',
    COLOR_GREY_MEDIUM: 'greyMedium',
    COLOR_GREY_DARK: 'greyDark',
    COLOR_DIVIDER: 'divider',
    COLOR_BORDER_LIGHT: 'borderLight',
    COLOR_SHADOW: 'shadow',
};

const cssVarToExperienceKey = (cssVar: string): string =>
    cssVar.slice(2).replace(/-/g, '_').toUpperCase();

/** Mapeo automático `--token-name` → `TOKEN_NAME` para tokens extendidos. */
const EXPERIENCE_TO_CSS_VAR: Record<string, string> = Object.fromEntries(
    Object.keys(DEFAULT_EXTENDED_CSS_VARS).map((cssVar) => [cssVarToExperienceKey(cssVar), cssVar]),
);

/** Keep legacy table/card CSS vars in sync with component.* defaults. */
const LEGACY_COMPONENT_ALIASES: Array<[string, string]> = [
    ['--component-table-header-bg', '--table-header-bg'],
    ['--component-table-header-color', '--table-header-color'],
    ['--component-table-cell-border', '--table-cell-border'],
    ['--component-card-shadow', '--abm-card-shadow'],
    ['--component-card-shadow-hover', '--abm-card-shadow-hover'],
    ['--component-card-border', '--abm-card-border'],
    ['--component-icon-chrome', '--color-chrome'],
];

const syncLegacyAliases = (cssVarOverrides: Record<string, string>): void => {
    for (const [componentVar, legacyVar] of LEGACY_COMPONENT_ALIASES) {
        if (cssVarOverrides[componentVar] && !cssVarOverrides[legacyVar]) {
            cssVarOverrides[legacyVar] = cssVarOverrides[componentVar];
        }
        if (cssVarOverrides[legacyVar] && !cssVarOverrides[componentVar]) {
            cssVarOverrides[componentVar] = cssVarOverrides[legacyVar];
        }
    }
};

/**
 * Mapea tokens de Experience (flat o nested components/features) a core + CSS vars.
 */
export const mapExperienceTokensToTheme = (
    tokens: ExperienceThemeTokens | NestedThemeTokens,
): ExperienceThemeMapping => {
    const flat = flattenThemeTokens(tokens as NestedThemeTokens);
    const mapped: Partial<FullThemeTokens> = {};
    const cssVarOverrides: Record<string, string> = {};

    for (const [key, value] of Object.entries(flat)) {
        if (!value) {
            continue;
        }

        const coreKey = EXPERIENCE_TO_CORE_TOKEN[key];
        if (coreKey) {
            mapped[coreKey] = value;
            if (key === 'COLOR_ACCENT' && !flat['COLOR_SECONDARY']) {
                mapped.secondary = value;
            }
            continue;
        }

        const cssVar = EXPERIENCE_TO_CSS_VAR[key];
        if (cssVar) {
            cssVarOverrides[cssVar] = value;
            continue;
        }

        // Feature overrides not in DEFAULT_EXTENDED (e.g. FEATURE_CASES_TABLE_ROW_ZEBRA_BG)
        if (key.startsWith('FEATURE_') || key.startsWith('COMPONENT_')) {
            const derived = `--${key.toLowerCase().replace(/_/g, '-')}`;
            cssVarOverrides[derived] = value;
        }
    }

    syncLegacyAliases(cssVarOverrides);
    return { tokens: mapped, cssVarOverrides };
};
